<script lang="ts">
  import { _ } from 'svelte-i18n';
  import { onMount } from 'svelte';
  import { fade, fly } from 'svelte/transition';

  let isOpen = $state(false);
  let buttonRef = $state<HTMLButtonElement | null>(null);
  let dropdownRef = $state<HTMLDivElement | null>(null);
  
  // Make rssFeeds reactive so translations update when language changes
  const rssFeeds = $derived([
    { 
      id: 'all', 
      label: $_('weekly.rss.all'),
      url: '/rss/weekly.xml',
      description: $_('weekly.rss.allDesc')
    },
    { 
      id: 'blog', 
      label: $_('weekly.rss.blog'),
      url: '/rss/weekly/blog.xml',
      description: $_('weekly.rss.blogDesc')
    }
  ]);
  
  function toggleDropdown(event: MouseEvent) {
    event.stopPropagation();
    isOpen = !isOpen;
  }
  
  function closeDropdown() {
    isOpen = false;
  }
  
  function copyToClipboard(url: string) {
    const fullUrl = window.location.origin + url;
    navigator.clipboard.writeText(fullUrl).then(() => {
      // Show feedback (you could add a toast here)
      const originalLabel = rssFeeds.find(f => f.url === url)?.label;
      if (originalLabel) {
        // Simple feedback - you might want to add a toast notification
        console.log(`Copied ${originalLabel} RSS feed URL`);
      }
    });
  }
  
  // Handle clicks outside
  onMount(() => {
    function handleClickOutside(event: MouseEvent) {
      if (!buttonRef?.contains(event.target as Node) && 
          !dropdownRef?.contains(event.target as Node)) {
        closeDropdown();
      }
    }
    
    function handleEscape(event: KeyboardEvent) {
      if (event.key === 'Escape' && isOpen) {
        closeDropdown();
      }
    }
    
    document.addEventListener('click', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    
    return () => {
      document.removeEventListener('click', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  });
</script>

<div class="rss-dropdown">
  <button 
    bind:this={buttonRef}
    class="rss-button" 
    class:rss-button--active={isOpen}
    onclick={toggleDropdown}
    title={$_('weekly.rss.title')}
    aria-label={$_('weekly.rss.title')}
    aria-expanded={isOpen}
    aria-haspopup="true"
  >
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
      <path d="M4 11a9 9 0 0 1 9 9" />
      <path d="M4 4a16 16 0 0 1 16 16" />
      <circle cx="5" cy="19" r="1" fill="currentColor" />
    </svg>
    <svg class="rss-button__chevron" width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" stroke-width="2">
      <path d="M3 4.5L6 7.5L9 4.5" />
    </svg>
  </button>
  
  {#if isOpen}
    <div 
      bind:this={dropdownRef}
      class="rss-dropdown__menu"
      transition:fly={{ y: -8, duration: 200 }}
    >
      <div class="rss-dropdown__header">
        <h3>{$_('weekly.rss.feedsTitle')}</h3>
        <p>{$_('weekly.rss.feedsDescription')}</p>
      </div>
      
      <div class="rss-dropdown__feeds">
        {#each rssFeeds as feed}
          <div class="rss-feed-item">
            <div class="rss-feed-item__content">
              <h4>{feed.label}</h4>
              <p>{feed.description}</p>
            </div>
            <div class="rss-feed-item__actions">
              <a 
                href={feed.url}
                class="rss-feed-item__link"
                title={$_('weekly.rss.subscribe')}
                aria-label="{$_('weekly.rss.subscribe')} - {feed.label}"
                onclick={closeDropdown}
              >
                <svg width="14" height="14" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path d="M3.293 3.293a1 1 0 011.414 0L10 8.586l5.293-5.293a1 1 0 111.414 1.414L11.414 10l5.293 5.293a1 1 0 01-1.414 1.414L10 11.414l-5.293 5.293a1 1 0 01-1.414-1.414L8.586 10 3.293 4.707a1 1 0 010-1.414z" clip-rule="evenodd" fill-rule="evenodd" transform="rotate(45 10 10)"/>
                </svg>
              </a>
              <button 
                class="rss-feed-item__copy"
                onclick={() => copyToClipboard(feed.url)}
                title={$_('weekly.rss.copyUrl')}
                aria-label="{$_('weekly.rss.copyUrl')} - {feed.label}"
              >
                <svg width="14" height="14" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path d="M8 3a1 1 0 011-1h2a1 1 0 110 2H9a1 1 0 01-1-1z"/>
                  <path d="M6 3a2 2 0 00-2 2v11a2 2 0 002 2h8a2 2 0 002-2V5a2 2 0 00-2-2 3 3 0 01-3 3H9a3 3 0 01-3-3z"/>
                </svg>
              </button>
            </div>
          </div>
        {/each}
      </div>
      
      <div class="rss-dropdown__footer">
        <p>{$_('weekly.rss.footerText')}</p>
      </div>
    </div>
  {/if}
</div>

<style>
  .rss-dropdown {
    position: relative;
  }
  
  .rss-button {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.25rem;
    width: 44px;
    height: 40px;
    padding: 0;
    background: var(--bg-secondary);
    border: 1px solid var(--border-light);
    border-radius: 8px;
    color: var(--text-secondary);
    cursor: pointer;
    transition: all 0.2s ease;
  }
  
  .rss-button:hover,
  .rss-button--active {
    border-color: var(--border-default);
    background: var(--hover-overlay);
    color: var(--text-primary);
  }
  
  .rss-button__chevron {
    transition: transform 0.2s ease;
  }
  
  .rss-button--active .rss-button__chevron {
    transform: rotate(180deg);
  }
  
  .rss-dropdown__menu {
    position: absolute;
    top: calc(100% + 8px);
    right: 0;
    width: 320px;
    background: var(--bg-primary);
    border: 1px solid var(--border-default);
    border-radius: 12px;
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
    z-index: 1000;
    overflow: hidden;
  }
  
  .rss-dropdown__header {
    padding: 1rem;
    border-bottom: 1px solid var(--border-light);
  }
  
  .rss-dropdown__header h3 {
    margin: 0 0 0.25rem;
    font-size: 0.875rem;
    font-weight: 600;
    color: var(--text-primary);
  }
  
  .rss-dropdown__header p {
    margin: 0;
    font-size: 0.75rem;
    color: var(--text-secondary);
  }
  
  .rss-dropdown__feeds {
    padding: 0.5rem;
  }
  
  .rss-feed-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.75rem;
    border-radius: 8px;
    transition: background 0.2s ease;
  }
  
  .rss-feed-item:hover {
    background: var(--bg-secondary);
  }
  
  .rss-feed-item__content {
    flex: 1;
    min-width: 0;
  }
  
  .rss-feed-item__content h4 {
    margin: 0 0 0.125rem;
    font-size: 0.8125rem;
    font-weight: 500;
    color: var(--text-primary);
  }
  
  .rss-feed-item__content p {
    margin: 0;
    font-size: 0.75rem;
    color: var(--text-tertiary);
  }
  
  .rss-feed-item__actions {
    display: flex;
    gap: 0.25rem;
    margin-left: 0.75rem;
  }
  
  .rss-feed-item__link,
  .rss-feed-item__copy {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    padding: 0;
    background: var(--bg-tertiary);
    border: 1px solid var(--border-light);
    border-radius: 6px;
    color: var(--text-secondary);
    cursor: pointer;
    transition: all 0.2s ease;
  }
  
  .rss-feed-item__copy {
    background: transparent;
    border: 1px solid var(--border-light);
  }
  
  .rss-feed-item__link:hover,
  .rss-feed-item__copy:hover {
    background: var(--bg-primary);
    border-color: var(--border-default);
    color: var(--text-primary);
  }
  
  .rss-dropdown__footer {
    padding: 0.75rem 1rem;
    background: var(--bg-secondary);
    border-top: 1px solid var(--border-light);
  }
  
  .rss-dropdown__footer p {
    margin: 0;
    font-size: 0.75rem;
    color: var(--text-tertiary);
    text-align: center;
  }
  
  @media (max-width: 640px) {
    .rss-dropdown__menu {
      right: auto;
      left: 50%;
      transform: translateX(-50%);
      width: 280px;
    }
  }
</style>